import React, { useCallback, forwardRef, type ForwardedRef } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import {
	COMPACT,
	HIDDEN,
	VISIBLE,
} from '@atlassian/jira-platform-inline-card-create/src/common/constants.tsx';
import TypeSelect, {
	type Props as TypeSelectProps,
	type SelectRef as TypeSelectRef,
} from '@atlassian/jira-platform-inline-card-create/src/ui/form/type-select/index.tsx';
import PlatformInlineIssueCreate from '@atlassian/jira-platform-inline-card-create/src/ui/main.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useStoreFields } from '../../../common/hooks/use-store-fields/main.tsx';
import { GlobalIssueCreateType } from '../../../model/modals/types';
import type { CardId, CardTypeId } from '../../../model/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { cardCreateMultipleRequest } from '../../../state/actions/cards/create';
import { setCardIdWithAboveICCOpen } from '../../../state/actions/cards/rich-card';
import { setInlineCreateSessionId } from '../../../state/actions/inline-create';
import { openModal } from '../../../state/actions/modals';
import { getIsBoardCrossProject, getIsCompanyManaged } from '../../../state/selectors/app';
import { getLastAppliedParentFilters } from '../../../state/selectors/card-create';
import { manageIssueTypesSelector } from '../../../state/selectors/card-type';
import { hasICCOpen as hasICCOpenSelector } from '../../../state/selectors/cards';
import { getFilters } from '../../../state/selectors/filters';
import {
	getCardTypesForICC,
	getInlineCreateSessionId,
} from '../../../state/selectors/inline-create';
import { issueParentsSelector } from '../../../state/selectors/issue-parent';
import { canEditBoardConfig } from '../../../state/selectors/permissions';

const isInSimplifyIccBundleVariation = () => {
	const [expConfigM2] = UNSAFE_noExposureExp('simplify_icc_bundle_m2');

	const isInVariationM2 = expConfigM2.get('cohort', 'not-enrolled') === 'variation';
	return isInVariationM2;
};

// Hacky way to generate optimistic issue id.
// Not ideal, but have to do it for now to react backwards compatibility with current ICC.
let lastCardId = 0;
const generateId = (): number => {
	lastCardId -= 1;
	return lastCardId;
};

export type Props = {
	isBetweenCards?: boolean;
	isOpenByDefault?: boolean;
	messages?: {
		title: string;
		description: string;
	}[];
	placeholderText?: string;
	triggerLabel?: string;
	ariaLabelledBy?: string;
	insertBefore?: CardId | null;
	listId: string;
	isFormVisible?: boolean;
};

const StyledTypeSelect = forwardRef((props: TypeSelectProps, ref: ForwardedRef<TypeSelectRef>) => {
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

	return (
		<TypeSelectWrapper shouldShowChevron={isCompanyManaged}>
			<TypeSelect {...props} ref={ref} />
		</TypeSelectWrapper>
	);
});

const localStorage = createLocalStorageProvider('backlog');
const SELECTED_ISSUE_TYPE_KEY = 'selectedIssueType';

const InlineCardCreate = ({
	isBetweenCards = false,
	isOpenByDefault = false,
	messages,
	insertBefore = null,
	listId,
	placeholderText = '',
	triggerLabel = '',
	ariaLabelledBy,
	isFormVisible,
}: Props) => {
	const initialCardTypes = useBacklogSelector(getCardTypesForICC);

	const parentId = useBacklogSelector(getLastAppliedParentFilters);
	const manageIssueTypesLink = useBacklogSelector((state) => {
		if (canEditBoardConfig(state)) {
			return manageIssueTypesSelector(state);
		}
		return null;
	});
	const enabledFilters = useBacklogSelector(getFilters);
	const hasICCOpen = useBacklogSelector((state) =>
		insertBefore != null ? hasICCOpenSelector(state, { cardId: insertBefore }) : false,
	);
	const issueParents = useBacklogSelector((state) => issueParentsSelector(state));
	const dispatch = useBacklogDispatch();

	const { storeFieldsFromCardCreateData, storeFieldsFromFetchIssuesData } = useStoreFields({
		issueParents,
	});
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const isBoardCrossProject = useBacklogSelector(getIsBoardCrossProject);
	const inlineCreateSessionId = useBacklogSelector(getInlineCreateSessionId);
	const defaultCardTypeId = localStorage.get(SELECTED_ISSUE_TYPE_KEY) || null;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onAutoOpen = useCallback(
		(sessionId: string) => {
			// start event for auto open icc
			const startAutoOpenICCAnalyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			// firing start event for icc that is auto open - same as onTriggerClickCallback
			// @ts-expect-error Second argument should be of format "actionSubject action"
			fireUIAnalytics(startAutoOpenICCAnalyticsEvent, 'triggerClicked', 'triggerButton', {
				formWasOpen: isFormVisible,
				triggerAppearance: isBetweenCards ? HIDDEN : VISIBLE,
				sessionId,
				isInSimplifyIccBundleVariation: isInSimplifyIccBundleVariation(),
				isAutoOpen: true,
			});
			dispatch(
				setInlineCreateSessionId({
					sessionId,
				}),
			);
		},
		[createAnalyticsEvent, dispatch, isBetweenCards, isFormVisible],
	);

	const onTriggerClick = (sessionId?: string) => {
		const shouldOpenGICModal =
			isCompanyManaged && (isBoardCrossProject || !initialCardTypes?.length);
		if (shouldOpenGICModal) {
			const fixVersions = enabledFilters.VERSION || [];
			const cardTypeId = initialCardTypes?.[0]?.id ?? null;
			dispatch(
				openModal({
					type: GlobalIssueCreateType,
					payload: {
						cardListId: listId,
						cardParentId: parentId,
						cardSummary: '',
						cardTypeId,
						rankBeforeCardId: insertBefore,
						fixVersions,
						sessionId: inlineCreateSessionId,
					},
				}),
			);
		} else {
			// Dispatch session id.
			if (sessionId) {
				dispatch(
					setInlineCreateSessionId({
						sessionId,
					}),
				);
			}

			dispatch(setCardIdWithAboveICCOpen(insertBefore));
		}
	};

	const onCancel = useCallback(() => {
		dispatch(setCardIdWithAboveICCOpen(null));
	}, [dispatch]);

	const onCreate = useCallback(
		(
			inlineCards: {
				summary: string;
				cardTypeId: CardTypeId;
			}[],
		) => {
			const { cardTypeId } = inlineCards[0];
			const cardTypeEntity = initialCardTypes.find((ct) => ct.id === cardTypeId);
			const fixVersions = enabledFilters.VERSION || [];
			const tempIds = inlineCards.map(() => generateId());

			const doesCardTypeHaveRequiredFields = !!cardTypeEntity?.hasRequiredFields;

			if (doesCardTypeHaveRequiredFields) {
				dispatch(
					openModal({
						type: GlobalIssueCreateType,
						payload: {
							cardListId: listId,
							cardParentId: parentId,
							// With requiredfield multiple card creation is disabled
							cardSummary: inlineCards[0].summary,
							cardTypeId: Number(cardTypeId),
							rankBeforeCardId: insertBefore,
							fixVersions,
							sessionId: inlineCreateSessionId,
						},
					}),
				);
				return;
			}

			/** Analytics: We indicate to the analytics for issue creation that the inline creation ended up finishing inline creation (With session ID) */
			const analyticsEvent = createAnalyticsEvent({
				action: 'created',
				actionSubject: 'issue',
				sessionId: inlineCreateSessionId,
			});

			dispatch(
				cardCreateMultipleRequest({
					tempIds,
					summaries: inlineCards.map((inlineCard) => inlineCard.summary),
					typeId: Number(cardTypeId),
					typeIcon: cardTypeEntity ? cardTypeEntity.iconUrl : '',
					parentId,
					fixVersions,
					insertBefore,
					listId,
					storeFieldsFromCardCreateData,
					storeFieldsFromFetchIssuesData,
					analyticsEvent,
				}),
			);

			localStorage.set(SELECTED_ISSUE_TYPE_KEY, cardTypeId);
		},
		[
			initialCardTypes,
			enabledFilters.VERSION,
			createAnalyticsEvent,
			dispatch,
			parentId,
			insertBefore,
			listId,
			storeFieldsFromCardCreateData,
			storeFieldsFromFetchIssuesData,
			inlineCreateSessionId,
		],
	);

	const onCancelWrapper = useCallback(() => {
		onCancel();
	}, [onCancel]);

	const onIccCreate = useCallback(
		(
			issues: {
				summary: string;
				cardTypeId: number | string;
			}[],
		) => {
			onCreate(
				issues.map((issue) => ({
					summary: issue.summary,
					cardTypeId: Number(issue.cardTypeId),
				})),
			);
		},
		[onCreate],
	);

	const isFormHidden = isCompanyManaged && (isBoardCrossProject || !initialCardTypes?.length);

	return (
		<PlatformInlineIssueCreate
			cardTypes={initialCardTypes}
			extraItems={
				(manageIssueTypesLink && [
					{
						url: manageIssueTypesLink,
					},
				]) ||
				undefined
			}
			numFilters={Object.keys(enabledFilters).length}
			onCreate={onIccCreate}
			onTriggerClick={onTriggerClick}
			onCancel={onCancelWrapper}
			onBlur={onCancelWrapper}
			appearance={COMPACT}
			triggerAppearance={isBetweenCards ? HIDDEN : VISIBLE}
			isFormVisibleDefault={isFormHidden ? false : isOpenByDefault || hasICCOpen}
			messages={messages}
			placeholderText={placeholderText}
			triggerLabel={triggerLabel}
			ariaLabelledBy={ariaLabelledBy}
			defaultCardTypeId={defaultCardTypeId}
			isFormVisible={isFormHidden ? false : isFormVisible}
			TypeSelect={StyledTypeSelect}
			onAutoOpen={onAutoOpen}
		/>
	);
};

export default InlineCardCreate;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeSelectWrapper = styled.div<{
	shouldShowChevron: boolean;
}>({
	// Spacing tokens are in use
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ shouldShowChevron }) =>
		shouldShowChevron ? token('space.500', '40px') : token('space.250', '20px'),
	borderLeft: '1px solid transparent', // this is used to match with the border of the card, we can set margin-left to 21px, but we won't be able to migrate to use the space token
});
